<template>
    <div class="text-xs-left">
        <template v-for="(attributeValues, attributeIndexname) in languageVariantAttributes">
            <color-selector
                v-if="attributeValues.value_type === 'color_select'"
                :items="getAttributeValuesArray(attributeValues.values_additions, attributeIndexname, false)"
                :key="`${attributeIndexname}-color-selector`"
                :label="attributeValues.label"
                :value="variantsConfig[attributeIndexname]"
                @input="onVariantAttributeChange(attributeIndexname, $event)"
            />

            <trigger-selector
                v-else-if="attributeValues.value_type === 'trigger_select'"
                class="mt-2"
                :items="attributeValuesToItems(attributeValues.values)"
                :available-items="getAttributeValuesArray(attributeValues.values, attributeIndexname, false)"
                :additional-items="attributeValuesToItems(attributeValues.values_additions)"
                :key="`${attributeIndexname}-trigger-selector`"
                :label="attributeValues.label"
                :value="variantsConfig[attributeIndexname]"
                @input="onVariantAttributeChange(attributeIndexname, $event)"
                :indexname="attributeIndexname"
                :icon="attributeValues.icon"
            />

            <variant-image-selector
                v-else-if="attributeValues.value_type === 'variant_image_select'"
                :items="getVariantsImages(attributeValues.values_additions, attributeIndexname)"
                :key="`${attributeIndexname}-variant-image-selector`"
                :label="attributeValues.label"
                :value="variantsConfig[attributeIndexname]"
                @input="onVariantAttributeChange(attributeIndexname, $event)"
            />

            <v-select
                v-else
                :items="getAttributeValuesArray(attributeValues.values, attributeIndexname)"
                :key="`${attributeIndexname}-standard-selector`"
                :label="attributeValues.label"
                :value="variantsConfig[attributeIndexname]"
                @input="onVariantAttributeChange(attributeIndexname, $event)"
            />
        </template>
    </div>
</template>

<script>
import intersection from 'lodash/intersection';
import { createNamespacedHelpers } from 'vuex';
import ColorSelector from './Selector/ColorSelector';
import TriggerSelector from './Selector/TriggerSelector';
import VariantImageSelector from './Selector/VariantImageSelector';

const parentModuleName = 'Product';
const { mapState, mapGetters, mapMutations } = createNamespacedHelpers(parentModuleName);

export default {
    name: 'ProductVariants',
    components: {
        ColorSelector,
        TriggerSelector,
        VariantImageSelector,
    },
    props: [
        'variants',
        'variantsIds',
        'value',
    ],
    data() {
        return {
            buyableProducts: null,
        }
    },
    created() {
        const buyableProducts = {};
        if (this.variants && this.variants.mapper) {
            const variantsMapper = this.variants.mapper;
            const variantsMapperKeys = Object.keys(variantsMapper);
            if (variantsMapperKeys.length) {
                for (const indexname of variantsMapperKeys) {
                    const variantAttributeValues = variantsMapper[indexname];

                    const ids = Object.values(variantAttributeValues).flat();
                    for (const id of ids) {
                        if (!buyableProducts.hasOwnProperty(id)) {
                            buyableProducts[id] = this.isProductBuyable(null, null, id);
                        }
                    }

                    if (Object.values(variantAttributeValues).length === 1) {
                        this.onVariantAttributeChange(indexname, Object.keys(variantAttributeValues)[0]);
                    } else if (this.$router.currentRoute.params.id != this.product.id) {
                        let valueFound = false;
                        Object.keys(variantAttributeValues).some(variantAttributeValue => {
                            const id = this.$router.currentRoute.params.id;
                            if (variantAttributeValues[variantAttributeValue].includes(parseInt(id))) {
                                this.setVariantsConfig({
                                    key: indexname,
                                    value: variantAttributeValue,
                                });
                                valueFound = true;
                            }
                            return valueFound;
                        });
                        if (valueFound === false) {
                            this.setVariantsConfig({
                                key: indexname,
                                value: null,
                            });
                        }
                    } else {
                        this.setVariantsConfig({
                            key: indexname,
                            value: null,
                        });
                    }
                }

                if (this.$router.currentRoute.params.id != this.currentProductId) {
                    if (this.product.variantsIds.includes(this.$router.currentRoute.params.id)) {
                        this.setCurrentProductId(this.$router.currentRoute.params.id);
                    }
                } else if (this.variantsIds.length) {
                    this.setCurrentProductId(null);
                }
            } else {
                this.resetVariantsConfig();
            }
        } else {
            this.resetVariantsConfig();
        }
        this.buyableProducts = buyableProducts;
    },
    beforeDestroy() {
        this.resetVariantsConfig();
    },
    computed: {
        ...mapState([
            'product',
            'variantsConfig',
        ]),
        languageVariantAttributes() {
            const lang = this.$language.getLanguage();
            if (this.variants && this.variants.variant_attributes) {
                return this.variants.variant_attributes[lang] ? this.variants.variant_attributes[lang] : [];
            }
            return [];
        },
        availableVariantIds() {
            const availableIds = {};
            let attributes = Object.keys(this.variantsConfig);
            attributes.sort((a, b) => {
                let aV = this.variantsConfig[a];
                let bV = this.variantsConfig[b];
                return ((!aV && !bV) || (aV && bV)) ? 0 : (!aV ? 1 : -1);
            });

            let count = 0;
            for (const indexname of attributes) {
                const previousIndexname = attributes[count - 1];
                const valueIds = this.variants.mapper[indexname];
                const selectedValue = this.variantsConfig[previousIndexname];

                const attrIds = {};
                const valueIdsKeys = Object.keys(valueIds);
                for (const value of valueIdsKeys) {
                    const ids = [];
                    for (const id of valueIds[value]) {
                        if (this.buyableProducts[id]) {
                            ids.push(id);
                        }
                    }

                    if (!attrIds[value]) {
                        attrIds[value] = [];
                    }

                    if (count === 0) {
                        for (const id of ids) {
                            if (!attrIds[value].includes(id)) {
                                attrIds[value].push(id);
                            }
                        }
                    } else {
                        let previousAvailableIds = [];
                        if (selectedValue) {
                            previousAvailableIds = availableIds[previousIndexname][selectedValue];
                        } else {
                            const availableIdsKeys = Object.keys(availableIds[previousIndexname]);
                            for (const availableIds1Value of availableIdsKeys) {
                                const availableIds1Ids = availableIds[previousIndexname][availableIds1Value];
                                for (const availableIds1Id of availableIds1Ids) {
                                    previousAvailableIds.push(availableIds1Id);
                                }
                            }
                        }

                        for (const id of ids) {
                            if (previousAvailableIds.includes(id) && !attrIds[value].includes(id)) {
                                attrIds[value].push(id);
                            }
                        }
                    }
                }

                availableIds[indexname] = attrIds;
                count++;
            }

            return availableIds || {};
        },
        currentProductId: {
            get() {
                return this.value || this.getCurrentProductId;
            },
            set(value) {
                this.setCurrentProductId(value);
            },
        },
        ...mapGetters({
            getCurrentProductId: 'currentProductId',
            getProductImage: 'getProductImage',
            isProductBuyable: 'isProductBuyable',
        }),
    },
    methods: {
        ...mapMutations({
            setCurrentProductIdMutation: 'setCurrentProductId',
        }),
        ...mapMutations([
            'setVariantsConfig',
            'resetVariantsConfig',
            'deleteVariantsConfig,',
            'setVariantIds',
        ]),
        setCurrentProductId(value) {
            if (this.value) {
                this.$emit('input', value);
            } else {
                this.setCurrentProductIdMutation(value);
            }
        },
        onVariantAttributeChange(indexname, value) {
            this.setVariantsConfig({
                key: indexname,
                value,
            });
            this.$nextTick(() => {
                this.setVariantIds(this.getVariantIds());
                this.$nextTick(() => {
                    let variantId = this.getVariantId();
                    if (variantId) {
                        this.currentProductId = variantId;
                    } else {
                        this.currentProductId = null;
                    }
                });
            });
        },
        getVariantIds() {
            if (this.variants && this.variants.mapper) {
                let variantIds;
                Object.keys(this.variantsConfig).forEach(indexname => {
                    const value = this.variantsConfig[indexname];
                    if (value && this.variants.mapper[indexname] && this.variants.mapper[indexname][value]) {
                        if (!variantIds) {
                            variantIds = this.variants.mapper[indexname][value];
                        } else {
                            variantIds = intersection(variantIds, this.variants.mapper[indexname][value]);
                        }
                    }
                });
                return variantIds || [];
            }
        },
        getVariantId() {
            let variantIds = this.getVariantIds();

            if (!variantIds.length || variantIds.length > 1) {
                return null;
            } else {
                return variantIds[0];
            }
        },
        getVariantsImages(values, indexname) {
            const attributeValues = this.getAttributeValuesArray(values, indexname, false);
            for (const attributeValue of attributeValues) {
                if (this.variants.mapper[indexname][attributeValue.value]) {
                    const [ productId ] = this.variants.mapper[indexname][attributeValue.value];
                    const image = this.getProductImage(productId);
                    if (image) {
                        attributeValue.image = image.small;
                    }
                }
            }
            return attributeValues;
        },
        getAttributeValuesArray(values, indexname, applyEmptyValue = true) {
            const valuesArray = [];
            if (applyEmptyValue === true) {
                valuesArray.push({
                    value: null,
                    text: this.$language.translate('- keine Auswahl -'),
                });
            }

            const availableVariantIds = {...this.availableVariantIds};
            const mapper = this.variants.mapper;

            Object.keys(values).forEach(i => {
                mapper[indexname][i].some(id => {
                    if (
                        availableVariantIds[indexname] &&
                        availableVariantIds[indexname][i].includes(id)
                    ) {
                        valuesArray.push({
                            value: i,
                            text: values[i],
                            buyable: this.buyableProducts[id],
                        });
                        return true;
                    }
                });
            });

            if (applyEmptyValue) {
                let selectorHasValue = false;
                valuesArray.some(selectorValue => {
                    if (selectorValue.value === this.variantsConfig[indexname]) {
                        selectorHasValue = true;
                    }
                    return selectorHasValue;
                });
                if (!selectorHasValue) {
                    this.setVariantsConfig({
                        key: indexname,
                        value: null,
                    });
                }
            }
            return valuesArray;
        },
        attributeValuesToItems(values) {
            let items = [];
            Object.keys(values).forEach((value) => {
                if (value && values[value]) {
                    items.push({
                        value: value,
                        text: values[value],
                    });
                }
            });

            return items;
        },
    },
    watch: {
        currentProductId(productId) {
            if (
                productId &&
                productId != this.product.id &&
                this.variants && this.variants.mapper
            ) {
                const variantsMapper = this.variants.mapper;
                for (const indexname of Object.keys(variantsMapper)) {
                    if (this.variantsConfig[indexname] === null || this.variantsConfig[indexname].value === null) {
                        const variantAttributeValues = variantsMapper[indexname];
                        Object.keys(variantAttributeValues).some(variantAttributeValue => {
                            if (variantAttributeValues[variantAttributeValue].includes(parseInt(productId))) {
                                this.setVariantsConfig({
                                    key: indexname,
                                    value: variantAttributeValue,
                                });
                            }
                        });
                    }
                }
            }
        }
    }
};
</script>
